"use strict";

(function () {
  class ImageComparisonSlider extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.currentPercent = 50;
      this.isDragging = false;
      this.activeTimeout = null;
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.initialize();
    }

    initialize() {
      try {
        this.cacheElements();
        this.addEventListeners();
        this.moveSlider();
        this.trackSliderMovement();
        this.isInitialized = true;
        this.setAttribute("data-is-init", "true");
      } catch (error) {
        console.error("Image Comparison Slider initialization failed:", error);
      }
    }

    /**
     * Cache DOM elements for better performance
     * Called once during initialization
     */
    cacheElements() {
      this.rangeInput = this.querySelector(".image-comparison__range");
      this.beforeImage = this.querySelector(
        ".image-comparison__slider-wrapper .image--before",
      );
      this.afterImage = this.querySelector(
        ".image-comparison__slider-wrapper .image--after",
      );
      this.sliderElement = this.querySelector(".image-comparison__slider");

      // Validate all required elements exist
      if (!this.rangeInput || !this.beforeImage || !this.sliderElement) {
        throw new Error("Required image comparison elements not found");
      }
    }

    /**
     * Attach event listeners for slider interaction
     */
    addEventListeners() {
      // Input event for range slider
      this.rangeInput.addEventListener("input", (e) => this.handleInput(e));

      // Change event for analytics/final state
      this.rangeInput.addEventListener("change", (e) => this.handleChange(e));

      // Mouse drag detection
      this.rangeInput.addEventListener("mousedown", () => {
        this.isDragging = true;
      });
      document.addEventListener("mouseup", () => {
        this.isDragging = false;
      });

      // Touch support for mobile
      this.rangeInput.addEventListener("touchstart", () => {
        this.isDragging = true;
      });
      document.addEventListener("touchend", () => {
        this.isDragging = false;
      });

      // Keyboard accessibility
      this.rangeInput.addEventListener("keydown", (e) =>
        this.handleKeyboard(e),
      );

      // Responsive behavior on window resize
      window.addEventListener(
        "resize",
        this.debounce(() => this.trackSliderMovement(), 250),
      );
    }

    /**
     * Handle range input events
     */
    handleInput(event) {
      const value = parseInt(event.target.value, 10);
      this.moveSlider(value);
      this.trackSliderMovement();
    }

    /**
     * Handle change event (when user releases slider)
     * Useful for analytics tracking
     */
    handleChange(event) {
      const changeEvent = new CustomEvent("comparison-changed", {
        detail: {
          percent: this.currentPercent,
          isDragging: this.isDragging,
        },
        bubbles: true,
        cancelable: true,
      });
      this.dispatchEvent(changeEvent);
    }

    /**
     * Handle keyboard navigation
     * Arrow keys, Home, and End keys
     */
    handleKeyboard(event) {
      const step = 1;
      let newValue = this.currentPercent;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowDown":
          newValue = Math.max(0, this.currentPercent - step);
          event.preventDefault();
          break;
        case "ArrowRight":
        case "ArrowUp":
          newValue = Math.min(100, this.currentPercent + step);
          event.preventDefault();
          break;
        case "Home":
          newValue = 0;
          event.preventDefault();
          break;
        case "End":
          newValue = 100;
          event.preventDefault();
          break;
        default:
          return;
      }

      this.rangeInput.value = newValue;
      this.moveSlider(newValue);
      this.trackSliderMovement();
    }

    /**
     * Update slider position and clip paths
     * @param {number} value - Slider value (0-100)
     */
    moveSlider(value = null) {
      // Use provided value or get from range input
      const sliderValue =
        value !== null ? value : parseInt(this.rangeInput.value, 10);
      this.currentPercent = Math.max(0, Math.min(100, sliderValue));

      // Update range input to sync value
      this.rangeInput.value = this.currentPercent;

      // Calculate clip paths
      const beforeClip = 100 - this.currentPercent;
      const afterClip = this.currentPercent;

      // Apply clip paths for before/after images
      this.beforeImage.style.clipPath = `inset(0 ${beforeClip}% 0 0)`;
      if (this.afterImage) {
        this.afterImage.style.clipPath = `inset(0 0 0 ${afterClip}%)`;
      }

      // Update slider indicator position using CSS variable
      const percent =
        ((this.currentPercent - this.rangeInput.min) /
          (this.rangeInput.max - this.rangeInput.min)) *
        100;
      this.sliderElement.style.setProperty("--percent", `${percent}%`);

      // Dispatch update event for external listeners
      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: { percent: this.currentPercent },
          bubbles: true,
        }),
      );
    }

    /**
     * Track slider movement state for visual feedback
     * Adds classes based on user interaction and screen size
     */
    trackSliderMovement() {
      // Add active state class
      this.classList.add("js-active");

      // Add high-value state on narrow screens
      const isNarrowScreen = window.innerWidth < 990;
      if (isNarrowScreen && this.currentPercent >= 50) {
        this.classList.add("slider-high-value");
      } else {
        this.classList.remove("slider-high-value");
      }

      // Remove active state after user stops interacting
      clearTimeout(this.activeTimeout);
      this.activeTimeout = setTimeout(() => {
        this.classList.remove("js-active");
      }, 500);
    }

    /**
     * Utility function: Debounce to limit function call frequency
     * @param {Function} func - Function to debounce
     * @param {number} delay - Delay in milliseconds
     * @returns {Function} Debounced function
     */
    debounce(func, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    }

    /**
     * Public API Methods for external control
     */

    /**
     * Set slider to specific percent
     * @param {number} value - Percentage value (0-100)
     */
    setPercent(value) {
      this.moveSlider(value);
    }

    /**
     * Get current slider position
     * @returns {number} Current percentage
     */
    getPercent() {
      return this.currentPercent;
    }

    /**
     * Reset slider to center (50%)
     */
    reset() {
      this.moveSlider(50);
      this.trackSliderMovement();
    }

    /**
     * Cleanup and destroy component
     */
    destroy() {
      clearTimeout(this.activeTimeout);
      this.isInitialized = false;
      this.setAttribute("data-is-init", "false");
    }

    /**
     * Lifecycle hook: Cleanup when element is removed
     */
    disconnectedCallback() {
      this.destroy();
    }
  }

  // Register custom element only if not already defined
  if (!customElements.get("image-comparison")) {
    customElements.define("image-comparison", ImageComparisonSlider);
  }

  /**
   * Auto-initialize elements with data attribute
   * Useful for non-custom-element support or lazy initialization
   */
  // document.addEventListener("DOMContentLoaded", () => {
  //   document
  //     .querySelectorAll('[data-component="image-comparison-slider"]')
  //     .forEach((el) => {
  //       if (el instanceof ImageComparisonSlider && !el.isInitialized) {
  //         el.initialize();
  //       }
  //     });
  // });
})();
